import { Modal, toast } from '@insight-health-ai/roseate/components';
import { Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import { useCreatePatientDocument } from 'src/hooks/patient';
import { PatientFileDocument } from 'src/components';
import { Check } from '@insight-health-ai/roseate/icons';
import { PatientDocument, ServiceRequest } from 'src/models';
import { useCreateServiceRequestFromDocuments } from 'src/hooks/serviceRequest';

type CreateDocumentReferralModalProps = {
  open: boolean;
  onClose: (result?: ServiceRequest) => void;
};

export const CreateDocumentReferralModal = ({
  open,
  onClose,
}: CreateDocumentReferralModalProps) => {
  const [attachments, setAttachments] = useState<
    { id: string; file: File; hasError?: boolean }[]
  >([]);
  const uploadedDocumentsRef = useRef<Record<string, PatientDocument>>({});

  const { mutateAsync: createPatientDocument } = useCreatePatientDocument();
  const { mutateAsync: createServiceRequestFromDocuments } =
    useCreateServiceRequestFromDocuments();

  const handleCreate = async () => {
    await Promise.all(
      attachments
        .filter(({ id }) => !uploadedDocumentsRef.current[id])
        .map(async ({ id, file }) => {
          try {
            const document = await createPatientDocument({
              type: 'file',
              file,
              source: 'referral',
              size: file.size,
            });
            uploadedDocumentsRef.current[id] = document;
          } catch (error) {
            toast.warning(`Failed to upload file: ${file.name}`);
            setAttachments((prev) =>
              prev.map((attachment) =>
                attachment.id === id
                  ? { ...attachment, hasError: true }
                  : attachment
              )
            );
          }
        })
    );

    const documents = Object.values(uploadedDocumentsRef.current);

    if (documents.length === 0) {
      toast.error('No valid documents attached');
      return;
    }

    try {
      const serviceRequest = await createServiceRequestFromDocuments({
        patientDocumentIds: Object.values(uploadedDocumentsRef.current).map(
          ({ patient_document_id }) => patient_document_id
        ),
      });
      toast.success('Request submitted successfully!');
      onClose?.(serviceRequest);
    } catch (error) {
      toast.error('Failed to create service request from documents');
    }
  };

  const validateFile = async (newFile: File) => {
    // check if file is already in attachments
    const similarFileExists = attachments.some(
      ({ file }) =>
        file.name === newFile.name &&
        file.type === newFile.type &&
        file.size === newFile.size
    );
    if (similarFileExists) {
      return await Modal.confirm({
        centered: true,
        title: 'Similar file already attached',
        content: 'Do you still want to upload?',
        primaryAction: {
          label: 'Proceed',
        },
      });
    }

    return true;
  };

  return (
    <Modal
      open={open}
      onClose={() => onClose?.()}
      title="Create request"
      primaryAction={{
        disabled: attachments.length === 0,
        label: 'Create',
        icon: <Check />,
        onClick: handleCreate,
      }}
    >
      <Upload.Dragger
        fileList={[]}
        name="files"
        accept=".jpg,.jpeg,.png,.pdf"
        beforeUpload={() => {
          return false;
        }}
        onChange={async ({ file }) => {
          const rawFile = file as unknown as File;

          if (await validateFile(rawFile)) {
            setAttachments((prev) => [
              ...prev,
              { id: `__temp__${rawFile.name}-${Date.now()}`, file: rawFile },
            ]);
          }
        }}
      >
        <p className="ant-upload-drag-icon">{<InboxOutlined />}</p>
        <p className="ant-upload-text text-[#00d090]">
          Click or drag file to this area to upload
          <p className="text-xs text-gray-500">images/pdf</p>
        </p>
      </Upload.Dragger>
      {attachments.length > 0 && (
        <div className="flex gap-2 mt-2">
          {attachments.map(({ id, file, hasError }) => (
            <PatientFileDocument
              key={id}
              source={'' as 'vca_patient_history' | 'pre_chart_history'}
              sendEvents={false}
              hasError={hasError}
              document={{
                patient_document_id: id,
                patient_id: 0,
                status: '',
                type: 'file',
                mime_type: file.type,
                name: file.name,
                size: file.size,
                source: 'referral',
                created_at: new Date().toISOString(),
                created_by: 'user',
                last_modified: new Date().toISOString(),
                content: null,
                description: null,
                parsed_content: null,
                url: null,
              }}
              canEdit={false}
              canDelete={true}
              onDelete={({ document }) => {
                setAttachments(
                  attachments.filter(
                    ({ id }) => id !== document.patient_document_id
                  )
                );
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};
